import rootInitialState from './initial-state';
import { createReducer } from './reducer-creator';
import {
  ADD_CITY,
  GET_APPLICATION,
  GET_NAVIGATION,
  SET_APP_INITIAL_DATA,
  SET_GEO_DATA,
  SET_TEMPLATE_DATA,
} from '../actions/action-types';

// Personalizar / initApp persist these names. The template payload historically
// used the shorter aliases — accept both so "Otros enlaces" match the preview.
const LEGAL_LINK_CATEGORY_NAMES = new Set(['enlaces-legales', 'links-legales']);
const UTIL_LINK_CATEGORY_NAMES = new Set(['enlaces-utiles-travelers', 'links-utiles']);

function detalleCategoryName(item) {
  return item?.categoria?.nombre || item?.tipoEnlace?.categoria?.nombre || '';
}

function filterFooterDetalleByCategory(detalle, categoryNames) {
  if (!Array.isArray(detalle)) return [];
  return detalle.filter((item) => categoryNames.has(detalleCategoryName(item)));
}

function getApplication(initialState, { payload }) {
  const { nombre, activa, web, token, currency, claims } = payload;
  const whatsappBusinessNumber = claims.displayPhoneNumber;
  return {
    ...initialState,
    nombre,
    applicationId: claims.applicationId,
    delegation: claims.delegation,
    tipoAplicacionId: claims.tipoAplicacionId,
    activa,
    web,
    token,
    currency,
    whatsappBusinessNumber,
  };
}

function getNavigation(initialState, action) {
  return {
    ...initialState,
    navigation: action.payload,
  };
}

function addCity(initialState, action) {
  const updated = initialState.cities.concat(action.payload);
  return {
    ...initialState,
    cities: updated,
  };
}

function setAppInitialData(initialState, { payload }) {
  const header = payload.header[0];
  const footer = payload.footer[0];

  const mainMenu = [...payload.mainMenu].sort((a, b) => parseInt(a.orden) - parseInt(b.orden));
  const { disabledLinks } = payload;
  const homeSection = mainMenu[0];

  const principalOffice = footer.sucursales.find((item) => item.casaCentral);
  const utilLink = filterFooterDetalleByCategory(footer.detalle, UTIL_LINK_CATEGORY_NAMES);
  const logos = footer.detalle.filter(
    (item) => item.categoria && item.categoria.nombre === 'logos-institucionales'
  );
  const fonts = footer.detalle.filter(
    (item) => item.categoria && item.categoria.nombre === 'fonts'
  );
  const legalLinks = filterFooterDetalleByCategory(footer.detalle, LEGAL_LINK_CATEGORY_NAMES);
  const colors = footer.detalle.filter(
    (item) =>
      item.categoria &&
      item.categoria?.descripcion?.toLowerCase().includes('colores') &&
      item.categoria.nombre === 'colores-templates'
  );

  const socialNetworks = footer.detalle.filter(
    (item) => item.categoria && item.categoria.nombre === 'rrss'
  );

  const institucionalLogos = footer.detalle.filter(
    (item) => item.categoria && item.categoria.nombre === 'logos-institucionales'
  );

  const headerImagen = footer.detalle.filter((item) => detalleCategoryName(item) === 'Header-Imagen');

  const headerPaquete = footer.detalle.filter(
    (item) => detalleCategoryName(item) === 'Buscador-Paquetes',
  );

  const headerVuelos = footer.detalle.filter((item) => detalleCategoryName(item) === 'Buscador-Aéreos');

  const headerHoteles = footer.detalle.filter(
    (item) => detalleCategoryName(item) === 'Buscador-Hoteles',
  );

  const newState = {
    ...initialState,
    navigation: {
      actualModule: '',
      homeSection,
    },
    templateData: {
      principalOffice,
      socialNetworks,
      mainMenu,
      disabledLinks,
      header,
      headerImagen,
      headerPaquete,
      headerVuelos,
      headerHoteles,
      colors,
      fonts,
      footer: {
        ...footer,
        institucionalLogos,
        legalLinks,
        utilLink,
        logos,
      },
    },
  };

  return newState;
}

function setGeoData(init, { payload }) {
  return {
    ...init,
    geo: {
      cities: {
        data: payload.cities,
        isReady: true,
        error: false,
      },
      localities: {
        data: payload.localities,
        isReady: true,
        error: false,
      },
    },
  };
}

function setTemplateData(init, { payload }) {
  if (!payload || !Array.isArray(payload) || payload.length === 0) {
    console.error('Template Data payload no válido:', payload);
    return { ...init };
  }

  const empresaTemplate = payload[0];
  const templateInfo = empresaTemplate?.template;
  const previousFooter = init.templateData?.footer || {};
  const footer = empresaTemplate?.footer || {};
  const header = empresaTemplate?.header || {};
  const detalle = Array.isArray(footer.detalle) && footer.detalle.length > 0
    ? footer.detalle
    : previousFooter.detalle;
  const principalOffice =
    empresaTemplate?.principalOffice ||
    footer?.sucursales?.find((item) => item.casaCentral) ||
    previousFooter?.sucursales?.find((item) => item.casaCentral) ||
    {};

  const rawPaisId = templateInfo?.pais_id ?? templateInfo?.paisId ?? init.paisId ?? 1;
  const paisId = Number(rawPaisId) || 1;

  const legalLinks = filterFooterDetalleByCategory(detalle, LEGAL_LINK_CATEGORY_NAMES);
  const utilLink = filterFooterDetalleByCategory(detalle, UTIL_LINK_CATEGORY_NAMES);

  const logos = (detalle || []).filter((item) => detalleCategoryName(item) === 'logos');

  const fonts = (detalle || []).filter((item) =>
    item.categoria?.descripcion?.toLowerCase().includes('fuente'),
  );

  const colors = (detalle || []).filter(
    (item) =>
      item.categoria?.descripcion?.toLowerCase().includes('colores') &&
      detalleCategoryName(item) === 'colores-templates',
  );

  const socialNetworks = (detalle || []).filter((item) => detalleCategoryName(item) === 'rrss');

  const institucionalLogos = (detalle || []).filter(
    (item) => detalleCategoryName(item) === 'logos-institucionales',
  );

  const headerImagen = (detalle || []).filter((item) => detalleCategoryName(item) === 'Header-Imagen');
  const headerPaquete = (detalle || []).filter((item) => detalleCategoryName(item) === 'Buscador-Paquetes');
  const headerVuelos = (detalle || []).filter((item) => detalleCategoryName(item) === 'Buscador-Aéreos');
  const headerHoteles = (detalle || []).filter((item) => detalleCategoryName(item) === 'Buscador-Hoteles');

  const disabledLinks = empresaTemplate?.disabledLinks ?? init.templateData?.disabledLinks ?? [];

  return {
    ...init,
    paisId,
    templateId: templateInfo?.id,
    templateImage: templateInfo?.templateImage?.url || null,
    empresaTemplateId: empresaTemplate?.id,
    empresaTemplateActivo: empresaTemplate?.activo,
    empresaId: empresaTemplate?.empresaId,
    // MUN-2237: the Personalizar settings blob (published via the intranet's
    // /configuration/:id/personalizar screen) already comes back on this same
    // empresaTemplate row — no separate fetch needed to know if/what an agency published.
    brandingSettings: empresaTemplate?.settings ?? null,
    templateNombre: templateInfo?.nombre,
    templateHtml: templateInfo?.html,
    templateData: {
      ...init.templateData,
      principalOffice:
        principalOffice && Object.keys(principalOffice).length
          ? principalOffice
          : init.templateData?.principalOffice,
      socialNetworks: socialNetworks.length ? socialNetworks : init.templateData?.socialNetworks,
      header: Object.keys(header).length ? header : init.templateData?.header,
      headerImagen: headerImagen.length ? headerImagen : init.templateData?.headerImagen,
      headerPaquete: headerPaquete.length ? headerPaquete : init.templateData?.headerPaquete,
      headerVuelos: headerVuelos.length ? headerVuelos : init.templateData?.headerVuelos,
      headerHoteles: headerHoteles.length ? headerHoteles : init.templateData?.headerHoteles,
      colors: colors.length ? colors : init.templateData?.colors,
      fonts: fonts.length ? fonts : init.templateData?.fonts,
      disabledLinks,
      footer: {
        ...previousFooter,
        ...footer,
        detalle: detalle || previousFooter.detalle,
        institucionalLogos: institucionalLogos.length
          ? institucionalLogos
          : previousFooter.institucionalLogos || [],
        legalLinks: legalLinks.length ? legalLinks : previousFooter.legalLinks || [],
        utilLink: utilLink.length ? utilLink : previousFooter.utilLink || [],
        logos: logos.length ? logos : previousFooter.logos || [],
      },
    },
  };
}

export default createReducer(rootInitialState.applicationState, {
  [GET_APPLICATION]: getApplication,
  [GET_NAVIGATION]: getNavigation,
  [ADD_CITY]: addCity,
  [SET_APP_INITIAL_DATA]: setAppInitialData,
  [SET_GEO_DATA]: setGeoData,
  [SET_TEMPLATE_DATA]: setTemplateData,
});
